import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShoppingCart, Plus, Minus, ChevronLeft, CheckCircle2,
  AlertCircle, Loader2, MapPin, Truck,
  UtensilsCrossed, ShoppingBag,
  ChevronRight, Star, Tag, Phone, User, Mail,
  MessageSquare, Package, X, Check, Clock, ChefHat, Receipt, PartyPopper,
  type LucideIcon,
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { publicApiError, publicErrorText } from '@/lib/public-api-error';
import { FilterChips } from '@/components/ui/filter-chips';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// ── Public API (no auth headers) ─────────────────────────────────────────────

const publicApi = {
  get: async (url: string) => {
    const res = await fetch(`/api/v1${url}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { message?: string; code?: string };
      throw publicApiError(err);
    }
    return res.json();
  },
  post: async (url: string, data: unknown) => {
    const res = await fetch(`/api/v1${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { message?: string | string[]; code?: string };
      throw publicApiError(err);
    }
    return res.json();
  },
};

// ── Types ─────────────────────────────────────────────────────────────────────

type OrderType = 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY';
type Step = 'type' | 'menu' | 'cart' | 'details' | 'done';

interface ModifierOption {
  id: string;
  name: string;
  priceAdjust: number;
}

interface Modifier {
  id: string;
  name: string;
  required: boolean;
  multiSelect: boolean;
  minSelect: number;
  maxSelect: number | null;
  sortOrder: number;
  options: ModifierOption[];
}

interface ProductVariant {
  id: string;
  name: string;
  sku: string;
  priceAdjustment: number;
  stockQuantity: number;
}

interface BundleComponent {
  componentId: string;
  quantity: number;
  component: { id: string; name: string; salePrice: number; imageUrl: string | null };
}

interface Product {
  id: string;
  name: string;
  description: string | null;
  salePrice: number;
  imageUrl: string | null;
  categoryId: string | null;
  trackStock: boolean;
  stockQuantity: number;
  variants: ProductVariant[];
  modifiers: Modifier[];
  bundleComponents: BundleComponent[];
  isFeaturedBundle: boolean;
}

interface Category {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  sortOrder: number;
}

interface Table {
  id: string;
  tableNo: string;
  capacity: number;
}

interface WidgetConfig {
  allowedOrderTypes: OrderType[];
  minOrderAmount: number;
  deliveryFee: number;
  estimatedPrepMinutes: number;
  acceptingOrders: boolean;
  welcomeMessage: string;
  successMessage: string;
  closedMessage: string;
  primaryColor: string;
  accentColor: string;
}

interface MenuData {
  tenant: { name: string; logoUrl: string | null; tagline: string | null };
  categories: Category[];
  products: Product[];
  featuredBundles: Product[];
  config: WidgetConfig;
}

interface CartModifierSelection {
  modifierId: string;
  modifierName: string;
  optionId: string;
  optionName: string;
  priceAdjust: number;
}

interface CartItem {
  cartId: string;
  product: Product;
  quantity: number;
  selectedVariant?: ProductVariant;
  modifierSelections: CartModifierSelection[];
  specialInstructions?: string;
}

// ── Modifier picker modal ─────────────────────────────────────────────────────

function ModifierModal({
  product,
  primaryColor,
  onConfirm,
  onClose,
}: {
  product: Product;
  primaryColor: string;
  onConfirm: (variant: ProductVariant | undefined, modifiers: CartModifierSelection[], instructions: string) => void;
  onClose: () => void;
}) {
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | undefined>(
    product.variants.length === 1 ? product.variants[0] : undefined,
  );
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [instructions, setInstructions] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  function toggleOption(modifierId: string, optionId: string, multiSelect: boolean, maxSelect: number | null) {
    setSelections(prev => {
      const current = prev[modifierId] ?? [];
      if (current.includes(optionId)) {
        return { ...prev, [modifierId]: current.filter(id => id !== optionId) };
      }
      if (!multiSelect) {
        return { ...prev, [modifierId]: [optionId] };
      }
      if (maxSelect && current.length >= maxSelect) {
        return prev;
      }
      return { ...prev, [modifierId]: [...current, optionId] };
    });
  }

  function handleConfirm() {
    const errs: string[] = [];
    for (const mod of product.modifiers) {
      const sel = selections[mod.id] ?? [];
      if (mod.required && sel.length < mod.minSelect) {
        errs.push(`${mod.name}: please select at least ${mod.minSelect} option(s)`);
      }
    }
    if (errs.length) { setErrors(errs); return; }

    const modifierSelections: CartModifierSelection[] = [];
    for (const mod of product.modifiers) {
      const selectedIds = selections[mod.id] ?? [];
      for (const optId of selectedIds) {
        const opt = mod.options.find(o => o.id === optId);
        if (opt) {
          modifierSelections.push({
            modifierId: mod.id,
            modifierName: mod.name,
            optionId: opt.id,
            optionName: opt.name,
            priceAdjust: opt.priceAdjust,
          });
        }
      }
    }
    onConfirm(selectedVariant, modifierSelections, instructions);
  }

  const basePrice =
    Number(product.salePrice) + Number(selectedVariant?.priceAdjustment ?? 0);
  const modifierTotal = Object.entries(selections).reduce((total, [modId, optIds]) => {
    const mod = product.modifiers.find(m => m.id === modId);
    if (!mod) return total;
    return total + optIds.reduce((s, optId) => {
      const opt = mod.options.find(o => o.id === optId);
      return s + Number(opt?.priceAdjust ?? 0);
    }, 0);
  }, 0);
  const totalPrice = basePrice + modifierTotal;

  return (
    <div className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="bg-card rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b flex items-start gap-3">
          {product.imageUrl && (
            <img src={product.imageUrl} alt={product.name} className="h-16 w-16 rounded-lg object-cover shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg">{product.name}</h3>
            {product.description && <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{product.description}</p>}
            <p className="text-sm font-medium mt-1" style={{ color: primaryColor }}>
              {formatCurrency(totalPrice)}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0 mt-1" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Variants */}
          {product.variants.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Choose Size / Variant</p>
              <div className="space-y-1">
                {product.variants.map(v => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setSelectedVariant(v)}
                    className={`w-full flex items-center justify-between p-2.5 rounded-lg border-2 text-sm ${selectedVariant?.id === v.id ? 'border-primary bg-primary/5' : 'border-border'}`}
                    style={selectedVariant?.id === v.id ? { borderColor: primaryColor } : {}}
                  >
                    <span>{v.name}</span>
                    {v.priceAdjustment !== 0 && (
                      <span className="text-xs text-muted-foreground">
                        {v.priceAdjustment > 0 ? '+' : ''}{formatCurrency(v.priceAdjustment)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Modifiers */}
          {product.modifiers.map(mod => (
            <div key={mod.id}>
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm font-semibold">{mod.name}</p>
                {mod.required && <span className="text-xs bg-destructive/10 text-destructive-text px-1.5 py-0.5 rounded">Required</span>}
                {!mod.required && <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">Optional</span>}
                {mod.maxSelect && <span className="text-xs text-muted-foreground">Max {mod.maxSelect}</span>}
              </div>
              <div className="space-y-1">
                {mod.options.map(opt => {
                  const selected = (selections[mod.id] ?? []).includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => toggleOption(mod.id, opt.id, mod.multiSelect, mod.maxSelect ?? null)}
                      className={`w-full flex items-center justify-between p-2.5 rounded-lg border text-sm ${selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'}`}
                      style={selected ? { borderColor: primaryColor } : {}}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-4 w-4 rounded-${mod.multiSelect ? 'sm' : 'full'} border-2 flex items-center justify-center ${selected ? 'bg-primary border-primary' : 'border-border'}`}
                          style={selected ? { background: primaryColor, borderColor: primaryColor } : {}}
                        >
                          {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        {opt.name}
                      </div>
                      {opt.priceAdjust !== 0 && (
                        <span className="text-xs text-muted-foreground">
                          {opt.priceAdjust > 0 ? '+' : ''}{formatCurrency(opt.priceAdjust)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Special instructions */}
          <div>
            <p className="text-sm font-semibold mb-1.5">Special Instructions</p>
            <Textarea
              value={instructions}
              onChange={e => setInstructions(e.target.value)}
              placeholder="e.g. No onions, extra sauce…"
              rows={2}
              maxLength={200}
              className="resize-none"
            />
          </div>

          {errors.length > 0 && (
            <div className="bg-destructive/10 rounded-lg p-3">
              {errors.map((e, i) => <p key={i} className="text-xs text-destructive-text">{e}</p>)}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t">
          <button
            type="button"
            onClick={handleConfirm}
            className="w-full py-3 rounded-xl text-primary-foreground font-semibold text-sm"
            style={{ background: primaryColor }}
          >
            Add to Cart — {formatCurrency(totalPrice)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bundle card ───────────────────────────────────────────────────────────────

function BundleCard({
  product,
  primaryColor,
  onAdd,
}: {
  product: Product;
  primaryColor: string;
  onAdd: (p: Product) => void;
}) {
  return (
    <div className="bg-warning/10 border-2 border-warning/30 rounded-2xl overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs bg-warning/20 text-foreground px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                <Star className="h-3 w-3" /> Combo Deal
              </span>
            </div>
            <h3 className="font-bold text-base">{product.name}</h3>
            {product.description && (
              <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{product.description}</p>
            )}
            {product.bundleComponents.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {product.bundleComponents.map(bc => (
                  <span key={bc.componentId} className="text-xs bg-card/70 border border-warning/30 rounded px-2 py-0.5">
                    {bc.quantity > 1 ? `${bc.quantity}× ` : ''}{bc.component.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          {product.imageUrl && (
            <img src={product.imageUrl} alt={product.name} className="h-20 w-20 rounded-xl object-cover shrink-0" />
          )}
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-lg font-bold" style={{ color: primaryColor }}>
            {formatCurrency(product.salePrice)}
          </span>
          <button
            type="button"
            onClick={() => onAdd(product)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-primary-foreground text-sm font-semibold"
            style={{ background: primaryColor }}
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Product card ──────────────────────────────────────────────────────────────

function ProductCard({
  product,
  primaryColor,
  cartCount,
  onAdd,
}: {
  product: Product;
  primaryColor: string;
  cartCount: number;
  onAdd: (p: Product) => void;
}) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl border hover:border-primary/40 transition bg-card">
      {product.imageUrl && (
        <img src={product.imageUrl} alt={product.name} className="h-16 w-16 rounded-lg object-cover shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-sm">{product.name}</h3>
        {product.description && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{product.description}</p>
        )}
        {product.modifiers.length > 0 && (
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
            <Tag className="h-3 w-3" />
            {product.modifiers.length} option{product.modifiers.length > 1 ? 's' : ''}
          </p>
        )}
        <div className="flex items-center justify-between mt-2">
          <span className="text-sm font-semibold" style={{ color: primaryColor }}>
            {formatCurrency(product.salePrice)}
          </span>
          <button
            type="button"
            onClick={() => onAdd(product)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-primary-foreground text-xs font-medium"
            style={{ background: primaryColor }}
          >
            {cartCount > 0 && <span className="bg-primary-foreground/30 rounded px-1">{cartCount}</span>}
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Order tracking ────────────────────────────────────────────────────────────

function OrderTracking({ slug, token, primaryColor, successMessage }: {
  slug: string;
  token: string;
  primaryColor: string;
  successMessage: string;
}) {
  const [order, setOrder] = useState<{ status: string; orderNo: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = () => {
      publicApi.get(`/public/ordering/${slug}/orders/${token}`)
        .then((data: { status: string; orderNo: string }) => setOrder(data))
        .catch(() => null)
        .finally(() => setLoading(false));
    };
    fetchOrder();
    const interval = setInterval(fetchOrder, 15000);
    return () => clearInterval(interval);
  }, [slug, token]);

  const statusConfig: Record<string, { Icon: LucideIcon; label: string; color: string }> = {
    OPEN: { Icon: Clock, label: 'Received', color: 'bg-info/10 text-info-text' },
    PREPARING: { Icon: ChefHat, label: 'Being Prepared', color: 'bg-warning/20 text-foreground' },
    READY: { Icon: CheckCircle2, label: 'Ready for Pickup', color: 'bg-success/10 text-success-text' },
    SERVED: { Icon: UtensilsCrossed, label: 'Served', color: 'bg-success/10 text-success-text' },
    BILLED: { Icon: Receipt, label: 'Completed', color: 'bg-muted text-foreground' },
    COMPLETED: { Icon: PartyPopper, label: 'Completed', color: 'bg-muted text-foreground' },
  };

  const steps = ['OPEN', 'PREPARING', 'READY'];
  const currentIdx = order ? steps.indexOf(order.status) : 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="p-4 max-w-lg mx-auto w-full space-y-6">
        <div className="text-center pt-6">
          <CheckCircle2 className="h-16 w-16 mx-auto mb-3" style={{ color: primaryColor }} />
          <h2 className="text-xl font-bold">Order Confirmed!</h2>
          <p className="text-muted-foreground text-sm mt-1">{successMessage}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : order ? (
          <>
            <div className="bg-card rounded-2xl p-4 border">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold">Order #{order.orderNo}</p>
                {statusConfig[order.status] && (
                  <span className={`text-xs px-2 py-1 rounded-full font-medium inline-flex items-center gap-1 ${statusConfig[order.status].color}`}>
                    {(() => { const StatusIcon = statusConfig[order.status].Icon; return <StatusIcon className="h-3.5 w-3.5" aria-hidden="true" />; })()}
                    {statusConfig[order.status].label}
                  </span>
                )}
              </div>

              {/* Progress bar */}
              <div className="flex items-center gap-1 mb-1">
                {steps.map((step, idx) => (
                  <div
                    key={step}
                    className={`flex-1 h-2 rounded-full transition-all ${idx <= currentIdx ? '' : 'bg-border'}`}
                    style={idx <= currentIdx ? { background: primaryColor } : {}}
                  />
                ))}
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>Received</span>
                <span>Preparing</span>
                <span>Ready</span>
              </div>
            </div>
            <p className="text-center text-xs text-muted-foreground">Auto-refreshes every 15 seconds</p>
          </>
        ) : null}
      </div>
    </div>
  );
}

// ── Main Page Component ───────────────────────────────────────────────────────

export function OnlineOrderPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embed') === '1';

  const [menuData, setMenuData] = useState<MenuData | null>(null);
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [step, setStep] = useState<Step>('type');
  const [orderType, setOrderType] = useState<OrderType>('TAKEAWAY');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [modifierTarget, setModifierTarget] = useState<Product | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [trackingToken, setTrackingToken] = useState('');

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [selectedTable, setSelectedTable] = useState('');
  const [notes, setNotes] = useState('');
  const [detailErrors, setDetailErrors] = useState<string[]>([]);

  const bodyRef = useRef<HTMLDivElement>(null);

  // Auto-resize for embedded iframe
  useEffect(() => {
    if (!isEmbedded) return;
    const observer = new ResizeObserver(() => {
      window.parent?.postMessage(
        { type: 'pos-ordering-height', height: document.body.scrollHeight },
        '*',
      );
    });
    observer.observe(document.body);
    return () => observer.disconnect();
  }, [isEmbedded]);

  // Load menu data
  useEffect(() => {
    if (!slug) { setError('Invalid link'); setLoading(false); return; }
    Promise.all([
      publicApi.get(`/public/ordering/${slug}/menu`),
      publicApi.get(`/public/ordering/${slug}/tables`),
    ])
      .then(([menu, tbls]: [MenuData, Table[]]) => {
        setMenuData(menu);
        setTables(tbls ?? []);
        if (menu.config?.allowedOrderTypes?.length > 0) {
          setOrderType(menu.config.allowedOrderTypes[0]);
        }
      })
      .catch((err: Error) => setError(err.message ?? 'Failed to load menu'))
      .finally(() => setLoading(false));
  }, [slug]);

  const { t } = useTranslation('pos');
  const cfg = menuData?.config;
  const primaryColor = cfg?.primaryColor ?? '#6366f1';

  function getCartCount(productId: string) {
    return cart.filter(i => i.product.id === productId).reduce((s, i) => s + i.quantity, 0);
  }

  function handleAddProduct(product: Product) {
    if (product.modifiers.length > 0 || product.variants.length > 1) {
      setModifierTarget(product);
    } else {
      addToCart(product, undefined, [], '');
    }
  }

  function addToCart(
    product: Product,
    variant: ProductVariant | undefined,
    modifierSelections: CartModifierSelection[],
    instructions: string,
  ) {
    const existing = cart.find(
      i =>
        i.product.id === product.id &&
        i.selectedVariant?.id === variant?.id &&
        i.modifierSelections.length === 0 &&
        modifierSelections.length === 0,
    );
    if (existing && modifierSelections.length === 0) {
      setCart(prev => prev.map(i => i.cartId === existing.cartId ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setCart(prev => [...prev, {
        cartId: `${product.id}-${Date.now()}`,
        product,
        quantity: 1,
        selectedVariant: variant,
        modifierSelections,
        specialInstructions: instructions || undefined,
      }]);
    }
    setModifierTarget(null);
  }

  function updateQty(cartId: string, delta: number) {
    setCart(prev =>
      prev.map(i => i.cartId === cartId ? { ...i, quantity: i.quantity + delta } : i)
        .filter(i => i.quantity > 0),
    );
  }

  function getItemTotal(item: CartItem) {
    const base =
      Number(item.product.salePrice) + Number(item.selectedVariant?.priceAdjustment ?? 0);
    const modTotal = item.modifierSelections.reduce((s, m) => s + Number(m.priceAdjust ?? 0), 0);
    return (base + modTotal) * item.quantity;
  }

  const cartSubtotal = cart.reduce((s, i) => s + getItemTotal(i), 0);
  const deliveryFee = orderType === 'DELIVERY' ? Number(cfg?.deliveryFee ?? 0) : 0;
  const cartTotal = cartSubtotal + deliveryFee;
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  async function submitOrder() {
    const errs: string[] = [];
    if (!customerName.trim()) errs.push('Name is required');
    if (!customerPhone.trim() || customerPhone.length < 7) errs.push('Valid phone number is required');
    if (orderType === 'DELIVERY' && !deliveryAddress.trim()) errs.push('Delivery address is required');
    if (orderType === 'DINE_IN' && !selectedTable) errs.push('Please select a table');
    setDetailErrors(errs);
    if (errs.length) return;

    setSubmitting(true);
    try {
      const res = await publicApi.post(`/public/ordering/${slug}/order`, {
        type: orderType,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        customerEmail: customerEmail.trim() || undefined,
        tableId: orderType === 'DINE_IN' ? selectedTable : undefined,
        deliveryAddress: orderType === 'DELIVERY' ? deliveryAddress.trim() : undefined,
        notes: notes.trim() || undefined,
        items: cart.map(item => ({
          productId: item.product.id,
          variantId: item.selectedVariant?.id ?? undefined,
          quantity: item.quantity,
          notes: item.specialInstructions ?? undefined,
          modifiers: item.modifierSelections.map(m => ({
            modifierName: m.modifierName,
            optionName: m.optionName,
            priceAdjust: m.priceAdjust,
          })),
        })),
      }) as { trackingToken: string };
      setTrackingToken(res.trackingToken);
      setStep('done');
    } catch (err: unknown) {
      setDetailErrors([publicErrorText(err, t('businessUnavailable'), 'Failed to place order. Please try again.')]);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Loading / Error states ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  if (error || !menuData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background px-4 text-center">
        <AlertCircle className="h-12 w-12 text-destructive-text mb-3" />
        <h2 className="font-semibold text-foreground">Online ordering unavailable</h2>
        <p className="text-muted-foreground text-sm mt-1">{error || 'This restaurant is not accepting online orders.'}</p>
      </div>
    );
  }

  if (!cfg?.acceptingOrders) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background px-4 text-center">
        <Clock className="h-12 w-12 text-muted-foreground mb-3" aria-hidden="true" />
        <h2 className="font-semibold text-foreground">{menuData.tenant.name}</h2>
        <p className="text-muted-foreground text-sm mt-2">{cfg?.closedMessage ?? "We're not accepting orders right now."}</p>
      </div>
    );
  }

  if (step === 'done' && trackingToken) {
    return (
      <OrderTracking
        slug={slug!}
        token={trackingToken}
        primaryColor={primaryColor}
        successMessage={cfg?.successMessage ?? 'Your order has been confirmed!'}
      />
    );
  }

  const filteredProducts = menuData.products.filter(
    p => !selectedCategory || p.categoryId === selectedCategory,
  );

  return (
    <div ref={bodyRef} className="min-h-screen bg-background">
      {/* ── Header ── */}
      <div className="sticky top-0 z-40 shadow-sm" style={{ background: primaryColor }}>
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            {menuData.tenant.logoUrl ? (
              <img src={menuData.tenant.logoUrl} alt={menuData.tenant.name} className="h-8 w-8 rounded-lg object-contain bg-primary-foreground/10" />
            ) : (
              <UtensilsCrossed className="h-5 w-5 text-primary-foreground/80" />
            )}
            <div className="min-w-0">
              <p className="text-primary-foreground font-semibold text-sm truncate">{menuData.tenant.name}</p>
              {menuData.tenant.tagline && <p className="text-primary-foreground/70 text-xs truncate">{menuData.tenant.tagline}</p>}
            </div>
          </div>
          {step !== 'type' && cartCount > 0 && (
            <button
              type="button"
              onClick={() => setStep('cart')}
              className="flex items-center gap-1.5 bg-primary-foreground/20 hover:bg-primary-foreground/30 px-3 py-1.5 rounded-full text-primary-foreground text-sm font-medium"
            >
              <ShoppingCart className="h-4 w-4" />
              {cartCount} · {formatCurrency(cartTotal)}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* ── STEP: Order Type Selection ── */}
        {step === 'type' && (
          <div className="px-4 py-8 space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground">{menuData.tenant.name}</h2>
              <p className="text-muted-foreground mt-1.5">{cfg?.welcomeMessage}</p>
            </div>
            <div className="space-y-3">
              {(cfg?.allowedOrderTypes ?? ['TAKEAWAY']).map(type => {
                const labels = {
                  TAKEAWAY: { Icon: ShoppingBag, title: 'Takeaway', desc: 'Order now, pick up when ready', color: 'border-primary/30 bg-primary/5' },
                  DELIVERY: { Icon: Truck, title: 'Delivery', desc: `Delivered to your door${deliveryFee > 0 ? ` · ${formatCurrency(deliveryFee)} fee` : ' · Free delivery'}`, color: 'border-success/30 bg-success/5' },
                  DINE_IN: { Icon: UtensilsCrossed, title: 'Dine In', desc: 'Order from your table', color: 'border-warning/30 bg-warning/10' },
                };
                const TypeIcon = labels[type].Icon;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => { setOrderType(type); setStep('menu'); }}
                    className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 text-start hover:shadow-md transition ${labels[type].color}`}
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-background/80 border border-border/50">
                      <TypeIcon className="h-6 w-6 text-foreground" aria-hidden="true" />
                    </span>
                    <div className="flex-1">
                      <p className="font-bold text-foreground">{labels[type].title}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">{labels[type].desc}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── STEP: Menu ── */}
        {step === 'menu' && (
          <div className="pb-24">
            <div className="px-4 pt-3 pb-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setStep('type')}
                className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Change
              </button>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-muted text-muted-foreground inline-flex items-center gap-1">
                {orderType === 'TAKEAWAY' && <ShoppingBag className="h-3.5 w-3.5" aria-hidden="true" />}
                {orderType === 'DELIVERY' && <Truck className="h-3.5 w-3.5" aria-hidden="true" />}
                {orderType === 'DINE_IN' && <UtensilsCrossed className="h-3.5 w-3.5" aria-hidden="true" />}
                {orderType === 'TAKEAWAY' ? 'Takeaway' : orderType === 'DELIVERY' ? 'Delivery' : 'Dine In'}
              </span>
            </div>

            {/* Featured Bundles */}
            {menuData.featuredBundles.length > 0 && (
              <div className="px-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="h-4 w-4 text-warning-text" />
                  <h2 className="font-bold text-foreground">Combo Deals</h2>
                </div>
                <div className="space-y-3">
                  {menuData.featuredBundles.map(bundle => (
                    <BundleCard
                      key={bundle.id}
                      product={bundle}
                      primaryColor={primaryColor}
                      onAdd={handleAddProduct}
                    />
                  ))}
                </div>
              </div>
            )}

            {menuData.categories.length > 0 && (
              <div className="sticky top-[60px] z-30 bg-background border-b px-4 py-2">
                <FilterChips
                  chips={[
                    { key: 'all', label: 'All' },
                    ...menuData.categories.map((cat) => ({ key: cat.id, label: cat.name })),
                  ]}
                  value={selectedCategory ?? 'all'}
                  onChange={(key) => setSelectedCategory(key === 'all' ? null : key)}
                  variant="outline"
                  size="sm"
                  accent={primaryColor}
                />
              </div>
            )}

            {/* Products */}
            <div className="px-4 pt-4 space-y-2">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No items in this category</p>
                </div>
              ) : (
                filteredProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    primaryColor={primaryColor}
                    cartCount={getCartCount(product.id)}
                    onAdd={handleAddProduct}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* ── STEP: Cart ── */}
        {step === 'cart' && (
          <div className="pb-24">
            <div className="px-4 pt-3 pb-2 flex items-center gap-2 border-b">
              <button type="button" onClick={() => setStep('menu')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                <ChevronLeft className="h-4 w-4" /> Menu
              </button>
              <h2 className="font-semibold ms-auto">Your Cart</h2>
            </div>

            {cart.length === 0 ? (
              <div className="text-center py-16">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Your cart is empty</p>
                <button
                  type="button"
                  onClick={() => setStep('menu')}
                  className="mt-3 text-sm font-medium"
                  style={{ color: primaryColor }}
                >
                  Browse menu
                </button>
              </div>
            ) : (
              <div className="px-4 pt-4 space-y-3">
                {cart.map(item => (
                  <div key={item.cartId} className="bg-card rounded-xl p-3 border">
                    <div className="flex items-start gap-3">
                      {item.product.imageUrl && (
                        <img src={item.product.imageUrl} alt={item.product.name} className="h-12 w-12 rounded-lg object-cover" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{item.product.name}</p>
                        {item.selectedVariant && (
                          <p className="text-xs text-muted-foreground">{item.selectedVariant.name}</p>
                        )}
                        {item.modifierSelections.map(m => (
                          <p key={m.optionId} className="text-xs text-muted-foreground">
                            {m.optionName}{m.priceAdjust !== 0 ? ` (+${formatCurrency(m.priceAdjust)})` : ''}
                          </p>
                        ))}
                        {item.specialInstructions && (
                          <p className="text-xs text-muted-foreground italic mt-0.5">"{item.specialInstructions}"</p>
                        )}
                      </div>
                      <div className="text-end shrink-0">
                        <p className="text-sm font-semibold">{formatCurrency(getItemTotal(item))}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <button
                            type="button"
                            onClick={() => updateQty(item.cartId, -1)}
                            className="h-6 w-6 rounded-full border flex items-center justify-center hover:bg-muted"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="text-sm w-6 text-center">{item.quantity}</span>
                          <button
                            type="button"
                            onClick={() => updateQty(item.cartId, 1)}
                            className="h-6 w-6 rounded-full border flex items-center justify-center hover:bg-muted"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Totals */}
                <div className="bg-card rounded-xl border p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span>{formatCurrency(cartSubtotal)}</span>
                  </div>
                  {orderType === 'DELIVERY' && deliveryFee > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Delivery fee</span>
                      <span>{formatCurrency(deliveryFee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold pt-1 border-t">
                    <span>Total</span>
                    <span>{formatCurrency(cartTotal)}</span>
                  </div>
                </div>

                {cfg?.minOrderAmount && cfg.minOrderAmount > 0 && cartSubtotal < cfg.minOrderAmount && (
                  <div className="bg-warning/10 border border-warning/30 rounded-xl p-3 text-sm text-foreground">
                    Min order: {formatCurrency(cfg.minOrderAmount)} · Add {formatCurrency(cfg.minOrderAmount - cartSubtotal)} more
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── STEP: Customer Details ── */}
        {step === 'details' && (
          <div className="pb-24">
            <div className="px-4 pt-3 pb-2 flex items-center gap-2 border-b">
              <button type="button" onClick={() => setStep('cart')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                <ChevronLeft className="h-4 w-4" /> Cart
              </button>
              <h2 className="font-semibold ms-auto">Your Details</h2>
            </div>
            <div className="px-4 pt-4 space-y-4">
              <div className="bg-card rounded-xl border p-4 space-y-3">
                <h3 className="font-medium text-sm text-foreground">Contact Information</h3>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1"><User className="h-3 w-3" />Full Name *</Label>
                  <Input
                    value={customerName}
                    onChange={e => setCustomerName(e.target.value)}
                    placeholder="Your full name"
                    className="w-full rounded-lg py-2.5"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />Phone Number *</Label>
                  <Input
                    type="tel"
                    value={customerPhone}
                    onChange={e => setCustomerPhone(e.target.value)}
                    placeholder="e.g. 0300-1234567"
                    className="w-full rounded-lg py-2.5"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />Email (optional)</Label>
                  <Input
                    type="email"
                    value={customerEmail}
                    onChange={e => setCustomerEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg py-2.5"
                  />
                </div>
              </div>

              {orderType === 'DELIVERY' && (
                <div className="bg-card rounded-xl border p-4 space-y-3">
                  <h3 className="font-medium text-sm text-foreground flex items-center gap-2">
                    <Truck className="h-4 w-4" /> Delivery Details
                  </h3>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />Delivery Address *</Label>
                    <Textarea
                      value={deliveryAddress}
                      onChange={e => setDeliveryAddress(e.target.value)}
                      placeholder="Full street address, city"
                      rows={2}
                      className="resize-none"
                    />
                  </div>
                </div>
              )}

              {orderType === 'DINE_IN' && tables.length > 0 && (
                <div className="bg-card rounded-xl border p-4 space-y-3">
                  <h3 className="font-medium text-sm text-foreground flex items-center gap-2">
                    <UtensilsCrossed className="h-4 w-4" /> Table Selection
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {tables.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setSelectedTable(t.id)}
                        className={`p-2 rounded-lg border-2 text-center text-sm ${selectedTable === t.id ? 'text-primary-foreground' : 'border-border text-foreground'}`}
                        style={selectedTable === t.id ? { background: primaryColor, borderColor: primaryColor } : {}}
                      >
                        <p className="font-semibold">#{t.tableNo}</p>
                        <p className="text-xs opacity-70">{t.capacity} seats</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-card rounded-xl border p-4 space-y-1.5">
                <Label className="text-xs text-muted-foreground flex items-center gap-1"><MessageSquare className="h-3 w-3" />Order Notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Any special requests or instructions?"
                  rows={2}
                  className="resize-none"
                />
              </div>

              {/* Order summary */}
              <div className="bg-card rounded-xl border p-4">
                <h3 className="font-medium text-sm mb-3">Order Summary</h3>
                {cart.map(item => (
                  <div key={item.cartId} className="flex justify-between text-sm py-1">
                    <span className="text-foreground">{item.quantity}× {item.product.name}</span>
                    <span className="text-foreground font-medium">{formatCurrency(getItemTotal(item))}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold text-sm pt-2 border-t mt-1">
                  <span>Total</span>
                  <span>{formatCurrency(cartTotal)}</span>
                </div>
              </div>

              {detailErrors.length > 0 && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3">
                  {detailErrors.map((e, i) => <p key={i} className="text-xs text-destructive-text">{e}</p>)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Modifier Modal ── */}
      {modifierTarget && (
        <ModifierModal
          product={modifierTarget}
          primaryColor={primaryColor}
          onConfirm={(variant, modifiers, instructions) => addToCart(modifierTarget, variant, modifiers, instructions)}
          onClose={() => setModifierTarget(null)}
        />
      )}

      {/* ── Bottom action bar ── */}
      {step !== 'type' && step !== 'done' && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-card border-t shadow-lg px-4 py-3 max-w-2xl mx-auto">
          {step === 'menu' && cartCount > 0 && (
            <button
              type="button"
              onClick={() => setStep('cart')}
              className="w-full py-3.5 rounded-xl text-primary-foreground font-semibold flex items-center justify-between px-5"
              style={{ background: primaryColor }}
            >
              <span className="bg-primary-foreground/20 rounded-lg px-2 py-0.5 text-sm">{cartCount} {cartCount === 1 ? t('itemSingular') : t('itemPlural')}</span>
              <span>View Cart</span>
              <span>{formatCurrency(cartTotal)}</span>
            </button>
          )}
          {step === 'cart' && cart.length > 0 && (
            <button
              type="button"
              onClick={() => setStep('details')}
              disabled={!!(cfg?.minOrderAmount && cfg.minOrderAmount > 0 && cartSubtotal < cfg.minOrderAmount)}
              className="w-full py-3.5 rounded-xl text-primary-foreground font-semibold disabled:opacity-50"
              style={{ background: primaryColor }}
            >
              Continue to Details
            </button>
          )}
          {step === 'details' && (
            <button
              type="button"
              onClick={submitOrder}
              disabled={submitting}
              className="w-full py-3.5 rounded-xl text-primary-foreground font-semibold flex items-center justify-center gap-2"
              style={{ background: primaryColor }}
            >
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
              Place Order · {formatCurrency(cartTotal)}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
